import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpertisesCard, { ExpertisesCardProps, ExpertiseData } from '../components/ExpertisesCard'

import { Expertise } from '@/core/models/expertise.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getExpertises, selectExpertises } from '@/redux/reducers/expertise.reducer'
import { isStaff } from '@/utils/type-guards'

export function ExpertisesCardContainer(props: Pick<ExpertisesCardProps, 'sx'>) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const expertises = useAppSelector(selectExpertises)

  const [selectedFilter, setSelectedFilter] = useState<string>(isStaff(authUser) ? (authUser?.status === 'PDS' ? 'sent' : 'today') : '')
  const expertisesDataByFilter: Record<string, ExpertiseData[]> = useMemo(() => {
    const expertiseMapper = (expertise: Expertise): ExpertiseData => ({
      id: expertise._id,
      creator: _.trim(`${expertise.createdBy?.userInfo?.name || ''} ${expertise.createdBy?.userInfo?.surname || ''}`),
      creatorGender: expertise.createdBy?.userInfo?.gender,
      doctor: expertise.medecin ? _.trim(`${expertise.medecin.userInfo?.name || ''} ${expertise.medecin.userInfo?.surname || ''}`) : undefined,
      doctorGender: expertise.medecin?.userInfo?.gender,
      isReviewable: authUser?._id === expertise.medecin?._id,
      patient: _.trim(`${expertise.patient?.userInfo?.name || ''} ${expertise.patient?.userInfo?.surname || ''}`),
      patientGender: expertise.patient?.userInfo?.gender,
      startDate: expertise.startDate,
      status: expertise.status,
    })

    return {
      today: _.map(
        _.filter(expertises, (expertise) => dayjs(expertise.startDate).isSame(dayjs(), 'day') && expertise.medecin?._id === authUser?._id),
        expertiseMapper
      ),
      received: _.map(
        _.filter(expertises, (expertise) => expertise.status === 'sent' && expertise.medecin?._id === authUser?._id),
        expertiseMapper
      ),
      reviewed: _.map(
        _.filter(expertises, (expertise) => expertise.status === 'reviewed' && expertise.createdBy?._id === authUser?._id),
        expertiseMapper
      ),
      sent: _.map(
        _.filter(expertises, (expertise) => expertise.status === 'sent' && expertise.createdBy?._id === authUser?._id),
        expertiseMapper
      ),
    }
  }, [expertises, authUser])

  const filters = useMemo(() => {
    if (!authUser || !isStaff(authUser)) return []

    function pickBy(arr: Array<{ id: string; title: string; activeColor: string }>, predicate: string[]) {
      return _.reduce(
        predicate,
        (result: typeof arr, value: string) => {
          const arrItem = _.find(arr, (item) => item.id === value)
          if (arrItem) result.push(arrItem)
          return result
        },
        []
      )
    }

    let array = [
      {
        id: 'today',
        title: t('page.home.expertises.selection.today'),
        activeColor: '#269CA6',
      },
      {
        id: 'received',
        title: t('page.home.expertises.selection.received'),
        activeColor: '#FF5C57',
      },
      {
        id: 'sent',
        title: t('page.home.expertises.selection.sent'),
        activeColor: '#FF5C57',
      },
      {
        id: 'reviewed',
        title: t('page.home.expertises.selection.reviewed'),
        activeColor: '#269CA6',
      },
    ]

    switch (authUser.status) {
      case 'PDS':
        array = pickBy(array, ['sent', 'reviewed'])
        break
      case 'MEDECIN':
        array = pickBy(array, ['today', 'received'])
        break
      default:
        break
    }

    return _.map(array, (filter) => ({
      ...filter,
      badgeContent: expertisesDataByFilter[filter.id].length,
      selected: selectedFilter === filter.id,
    }))
  }, [authUser, expertisesDataByFilter, selectedFilter, t])

  useEffect(() => {
    dispatch(
      getExpertises(
        {},
        {
          limit: 10,
          order: 'DESC',
          orderBy: 'startDate',
        }
      )
    )
  }, [dispatch])

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter)
  }

  let emptyMessage = ''
  switch (selectedFilter) {
    case 'today':
      emptyMessage = t('page.home.expertises.noExpertiseTodayMessage')
      break
    case 'received':
    case 'sent':
      if (isStaff(authUser)) emptyMessage = t(`page.home.expertises.noExpertiseValidationMessage.${authUser?.status.toLowerCase()}`)
      break
    case 'reviewed':
      emptyMessage = t('page.expertise.noExpertisesreviewedRequest')
      break
    default:
      emptyMessage = ''
      break
  }

  return (
    <ExpertisesCard
      {...props}
      expertises={expertisesDataByFilter[selectedFilter]}
      emptyMessage={emptyMessage}
      filters={filters}
      isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
      onFilterChange={handleFilterChange}
    />
  )
}

export default ExpertisesCardContainer
